import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import axios from "axios";

import { changeStringField } from "redux/auth/slice";

import { authApi } from "redux/auth/api";
import { userApi } from "redux/user/api";

import { STORAGE_AUTH_TOKEN } from "constants/variables";

import * as authSelectors from "redux/auth/selectors";

import { useGTM } from "hooks/useGTM";
import { useCurrency } from "hooks/useCurrency";
import { useTogglePopup } from "hooks/useTogglePopup";
import { useAuthStore } from "stores/auth";

import { getLocalStorageItem, setLocalStorageItem } from "utils/localStorage";

import { GET_POPUP_TYPES } from "constants/popupConfig";

export const useSocialAuth = () => {
  const [searchParams] = useSearchParams();
  const [telegramAuthError, setTelegramAuthError] = useState("");
  const [signIn] = authApi.useLoginMutation();
  const [signUp, { isLoading }] = authApi.useRegisterMutation();

  const dispatch = useDispatch();
  const location = useLocation();
  const GTM = useGTM();
  const queryClient = useQueryClient();
  const defaultCurrency = useCurrency();
  const { openPopup, closePopup } = useTogglePopup();
  const navigate = useNavigate();
  const setIsAfterAuthCheck = useAuthStore(
    (state) => state.setIsAfterAuthCheck,
  );
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { login, password, currencyCode } = useSelector(authSelectors.all);
  const token = getLocalStorageItem(STORAGE_AUTH_TOKEN);
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { refetch, isSuccess } = userApi.useGetProfileQuery(
    token ? "" : skipToken,
  );

  const from = searchParams.get("from");
  const hash = searchParams.get("hash");
  const currencyParam = searchParams.get("currency");
  const didRegister = useRef(false);

  useEffect(() => {
    if (token) {
      refetch();
    } else {
      setTimeout(() => {
        setIsAfterAuthCheck(true);
      }, 100);
    }
  }, [token, refetch, setIsAfterAuthCheck]);

  useEffect(() => {
    if (didRegister.current) {
      return;
    }

    if (from === "sign-in") {
      return;
    }

    if (!hash) {
      return;
    }

    const currency = currencyParam || currencyCode || defaultCurrency;

    if (!currency) {
      return;
    }

    didRegister.current = true;

    const getTelegramToken = async () => {
      try {
        const endpoint = "/v1/auth/register";

        const response = await axios.post(
          endpoint,
          {
            userTelegramData: Object.fromEntries(
              Array.from(searchParams.entries()).filter(
                ([key]) => !["popup", "currency", "from"].includes(key),
              ),
            ),
            authType: "telegram",
            currencyCode: currency,
            languageId: 6,
            depositUrl: "https://www.telegram.com/",
            welcomeBonusUrl: "https://www.telegram.com/",
          },
          {
            headers: { "Content-Type": "application/json" },
          },
        );

        // eslint-disable-next-line no-console
        console.log("response.data", response.data);

        const { login: userEmail, tempPassword, authToken } = response.data;

        if (userEmail && tempPassword) {
          const submitData = await signIn({
            login: userEmail,
            password: tempPassword,
          });

          setLocalStorageItem("socialTempPassword", tempPassword);

          GTM.onSocialRegistrationSuccess("telegram");

          if ("data" in submitData) {
            navigate(
              `?popup=password-registration-confirm&userEmail=${encodeURIComponent(
                userEmail,
              )}`,
            );
          }
        } else if (authToken) {
          setLocalStorageItem(STORAGE_AUTH_TOKEN, authToken);
          navigate("/");
        }
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error("error", error.message);
        setTelegramAuthError(error?.response?.data?.message);
      }
    };

    getTelegramToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    from,
    hash,
    currencyParam,
    currencyCode,
    defaultCurrency,
    signIn,
    navigate,
    searchParams,
  ]);

  return {
    telegramAuthError,
    setTelegramAuthError,
    isLoading,
    signUp,
    queryClient,
    openPopup,
    closePopup,
    navigate,
    location,
    dispatch,
    changeStringField,
    GET_POPUP_TYPES,
  };
};
